import { useQuery } from "@apollo/client";
import _, { debounce } from "lodash";
import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { removeEmptyValues } from "../../../utils/commons";

const ListingComponentContext = createContext();
const DELAY_REFETCH = 500; // Debounce delay in milliseconds

const ListingComponentProvider = ({
  children,
  query,
  extraVariables = {},
  fetchPolicy = "cache-and-network",
  queryKey,
  initialFilterData = {},
  noPagination,
  offsetBasedPagination = false,
  filterTransform = null, // Optional function to transform filter data before query
  navigationPath = null,
  initialFetch = true,
  shouldRemoveOffsetInFilter = false,
}) => {
  // State for filters
  const [filterData, setFilterData] = useState(initialFilterData);
  const [debouncedFilterData, setDebouncedFilterData] = useState({});
  const [debouncedFilterFormData, setDebouncedFilterFormData] = useState({});
  const [allFiltersData, setAllFiltersData] = useState({});
  const allFiltersDataRef = useRef(allFiltersData);
  const [shouldFetch, setShouldFetch] = useState(initialFetch);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    rowsCount: noPagination ? null : 20,
  });

  // References and view-related states
  const allRecords = useRef([]);
  const [viewableRecords, setViewableRecords] = useState([]);
  const [viewType, setViewType] = useState("grid");
  const [viewAllFilters, setViewAllFilters] = useState(false);
  const [totalPageCount, setTotalPageCount] = useState(0);
  const [isRefetching, setIsRefetching] = useState(false);

  // Apply filter transformation if provided
  const combinedFilterData = {
    ...filterData,
    ...debouncedFilterData,
  };

  const transformedFilterData = filterTransform
    ? filterTransform(combinedFilterData)
    : combinedFilterData;

  const variables = {
    filter: transformedFilterData,
    ...extraVariables,
    page: noPagination ? undefined : pagination.currentPage - 1,
    ...(offsetBasedPagination && {
      offset: pagination.currentPage - 1,
      limit: pagination.rowsCount,
    }),
  };

  if (shouldRemoveOffsetInFilter) {
    delete variables.filter.offset;
    delete variables.filter.limit;
  }
  // Fetch data using Apollo Client's useQuery
  const {
    data,
    loading,
    error,
    refetch: refetchData,
  } = useQuery(query, {
    variables,
    fetchPolicy,
    skip: !shouldFetch,
  });

  // Wrapper for refetch to track refetching state
  const refetch = useCallback(async () => {
    setIsRefetching(true);
    try {
      await refetchData();
    } finally {
      setIsRefetching(false);
    }
  }, [refetchData]);

  const filteredRecords = useMemo(() => data?.[queryKey] || [], [data, queryKey]);

  // Store all records when data is first loaded
  useEffect(() => {
    if (!allRecords.current.length && data?.[queryKey]) {
      allRecords.current = data[queryKey];
    }
  }, [data, queryKey]);

  // Reset pagination to first page
  const resetPagination = useCallback(
    () => setPagination((state) => ({ ...state, currentPage: 1, offset: 0 })),
    []
  );

  useEffect(() => {
    if (navigationPath) {
      setFilterData(initialFilterData);
      setAllFiltersData(initialFilterData);
      setViewableRecords([]);
      setViewAllFilters(false);
      setDebouncedFilterFormData({});
      setDebouncedFilterData({});
      resetPagination();
      refetch();
    }
  }, [navigationPath, initialFilterData, refetch, resetPagination]);

  // Check if the object contains an 'items' array
  const hasItems = (obj) => obj.items && Array.isArray(obj.items);

  // Calculate total records count
  const getTotalRecords = useCallback((filteredRecords, queryTotalRecords) => {
    return (
      queryTotalRecords ||
      (hasItems(filteredRecords)
        ? filteredRecords.items.length
        : filteredRecords.length || 0)
    );
  }, []);

  // Process and set viewable records based on pagination
  const handleData = useCallback(
    (records) => {
      if (noPagination) {
        setViewableRecords(records);
      } else {
        const queryTotalRecords = records.total;
        const start = queryTotalRecords
          ? 0
          : (pagination.currentPage - 1) * pagination.rowsCount;
        const end = start + pagination.rowsCount;
        const paginatedRecords = hasItems(records)
          ? records.items.slice(start, end)
          : records.slice(start, end);

        setViewableRecords(paginatedRecords);
        setTotalPageCount(
          Math.ceil(
            getTotalRecords(records, queryTotalRecords) / pagination.rowsCount
          )
        );
      }
    },
    [noPagination, pagination, getTotalRecords]
  );

  // Update viewable records when data or error state changes
  useEffect(() => {
    if (data?.[queryKey]) {
      handleData(data[queryKey]);
    } else if (error) {
      setViewableRecords([]);
    }
  }, [data, error, noPagination, pagination, queryKey, handleData]);

  // Debounced refetch for filtering
  const delayedRefetch = useMemo(
    () =>
      debounce((value) => {
        setDebouncedFilterData(removeEmptyValues({ ...value }));
        resetPagination();
      }, DELAY_REFETCH),
    [resetPagination]
  );

  const isFilterSameAsInitial = useCallback(
    (filters) =>
      _.isEqual(removeEmptyValues(filters), removeEmptyValues(initialFilterData)),
    [initialFilterData]
  );

  // Cleanup debounce on unmount
  useEffect(() => () => delayedRefetch.cancel(), [delayedRefetch]);

  // Handle filter changes with debounce
  const handleDebouncedFilterChange = useCallback(
    (formData) => {
      const cleanedData = removeEmptyValues(formData);
      setFilterData({
        ...filterData,
      });
      setDebouncedFilterFormData(cleanedData);

      const isEmpty = _.isEmpty(formData);
      const isDifferentFromInitial = !isFilterSameAsInitial({
        ...filterData,
        ...formData,
      });

      if (!initialFetch && isEmpty) {
        setViewableRecords([]);
      }

      if (initialFetch) {
        delayedRefetch(formData);
        return;
      }

      if (isDifferentFromInitial) {
        if (!shouldFetch) setShouldFetch(true);
        delayedRefetch(formData);
      } else {
        setShouldFetch(false);
      }
    },
    [delayedRefetch, filterData, shouldFetch, initialFetch, isFilterSameAsInitial]
  );

  // Handle all filter changes
  const handleAllFiltersChange = useCallback((data) => {
    const cleanedData = _.omitBy(
      data,
      (value) => (_.isArray(value) && _.isEmpty(value)) || value === ""
    );
    setAllFiltersData(cleanedData);
  }, []);

  // Handle Apply filter changes
  const handleApplyFilters = useCallback(() => {
    const removedFilters = Object.keys(allFiltersDataRef.current).filter(
      (key) => !Object.keys(allFiltersData).includes(key)
    );
    setFilterData((prev) =>
      _.omit(
        { ...prev, ...allFiltersData, ...(!offsetBasedPagination && { offset: 0 }) },
        removedFilters
      )
    );

    allFiltersDataRef.current = allFiltersData;
    resetPagination();
    delayedRefetch(allFiltersData);
    setViewAllFilters(false);
  }, [delayedRefetch, allFiltersData, resetPagination, offsetBasedPagination]);

  const handleClearFilters = useCallback(() => {
    setFilterData(initialFilterData);
    setDebouncedFilterData({});
    setDebouncedFilterFormData({});
    setAllFiltersData({});
    resetPagination();

    if (!initialFetch) setShouldFetch(false);
  }, [initialFilterData, resetPagination, initialFetch]);

  const contextValue = useMemo(
    () => ({
      filterData,
      debouncedFilterFormData,
      debouncedFilterData,
      setDebouncedFilterData,
      handleDebouncedFilterChange,
      pagination,
      viewType,
      totalPageCount,
      setTotalPageCount,
      handleClearFilters,
      handleApplyFilters,
      handleAllFiltersChange,
      allFiltersData,
      viewAllFilters,
      setViewAllFilters,
      setFilterData,
      setViewType,
      setPagination,
      setViewableRecords,
      resetPagination,
      refetch,
      data,
      allRecords: allRecords.current,
      filteredRecords,
      viewableRecords,
      loading: loading || isRefetching,
      error,
    }),
    [
      filterData,
      debouncedFilterData,
      debouncedFilterFormData,
      pagination,
      viewType,
      totalPageCount,
      allFiltersData,
      viewAllFilters,
      data,
      filteredRecords,
      viewableRecords,
      loading,
      isRefetching,
      error,
      handleDebouncedFilterChange,
      handleAllFiltersChange,
      handleClearFilters,
      handleApplyFilters,
      refetch,
      resetPagination,
      setViewableRecords,
    ]
  );

  return (
    <ListingComponentContext.Provider value={contextValue}>
      {children}
    </ListingComponentContext.Provider>
  );
};

ListingComponentProvider.propTypes = {
  children: PropTypes.node.isRequired,
  query: PropTypes.object.isRequired,
  extraVariables: PropTypes.object,
  fetchPolicy: PropTypes.string,
  queryKey: PropTypes.string.isRequired,
  initialFilterData: PropTypes.object,
  noPagination: PropTypes.bool,
};

export { ListingComponentContext, ListingComponentProvider };
